// RecorderProvider — держит единственный экземпляр recorder-движка на уровне
// __root.tsx, чтобы запись лекции продолжалась при SPA-навигации между
// разделами приложения (фоновая запись + pill в шапке).
//
// Проблема, которую решает: раньше `useRecorder()` вызывался прямо в route
// `/recordings/new`. При уходе с роута компонент размонтировался → cleanup-
// эффект останавливал микрофон → запись прерывалась. Теперь движок живёт в
// провайдере на корне (не размонтируется при навигации), а `/recordings/new`
// и `LiveBanner` потребляют один и тот же handle через `useRecorderHandle()`.
//
// Ограничение web-платформы: полная перезагрузка страницы (F5) убивает
// getUserMedia-stream. UI даёт «Продолжить» (continueExisting — новый mic +
// offset sequence на ту же lecture_session), «Завершить»/«Удалить».
// Cron НЕ запускает processRecording сам — только помечает пустые (0 чанков)
// abandoned как failed. SPA-навигация запись НЕ прерывает.

import { createContext, type ReactNode, useContext } from "react";
import { type UseRecorderHandle, useRecorder } from "@/hooks/use-recorder";

const RecorderContext = createContext<UseRecorderHandle | null>(null);

export function RecorderProvider({ children }: { children: ReactNode }): React.ReactElement {
	const recorder = useRecorder();
	return <RecorderContext.Provider value={recorder}>{children}</RecorderContext.Provider>;
}

/**
 * Доступ к глобальному recorder-движку. Используется на `/recordings/new`
 * (start/pause/resume/stop/cancel), stalled detail (`continueExisting`) и в
 * `LiveBanner` (pause/stop/возврат).
 */
export function useRecorderHandle(): UseRecorderHandle {
	const ctx = useContext(RecorderContext);
	if (!ctx) {
		throw new Error("useRecorderHandle must be used within <RecorderProvider>");
	}
	return ctx;
}
